import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import {
  changeShortcut,
  createShortcut,
  fetchShortcuts,
  type ShortcutDetailShape
} from 'Services/SalesManagement/Features/Shortcut/shortcutSlice'
import { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import LinkIcon from '@mui/icons-material/Link'
import { Box, InputAdornment, TextField } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, type FormEvent, useEffect, useRef, useState } from 'react'
import { t } from 'ttag'

const SaveShortcutDialog: FC<{
  data: ShortcutDetailShape
  open: boolean
  loading: boolean
  handleClose: () => void
}> = ({ data, open, loading, handleClose }) => {
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)

  const initialState: ShortcutDetailShape = {
    id: null,
    title_fi: '',
    link: '',
    description_fi: '',
    order: 0
  }

  const [shortcutState, setShortcutState] = useState<ShortcutDetailShape>(data || initialState)

  const errorsDefaultState = {
    title_fi: [],
    link: [],
    description_fi: [],
    order: []
  }

  const isNewInstance = !data?.id

  const [errors, setErrors] = useState(errorsDefaultState)

  // set existing data to form fields and empty errors when modal opens
  useEffect(() => {
    if (open) {
      setErrors(errorsDefaultState)
      if (isNewInstance) {
        setShortcutState(initialState)
      } else {
        setShortcutState(data)
      }
    }
  }, [open, data])

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSaveSuccess = (isCreateAction: boolean) => {
    handleClose()
    dispatch(fetchShortcuts())
    if (isCreateAction) {
      SnackbarUtils.success(t`Shortcut successfully added.`)
    } else {
      SnackbarUtils.success(t`Shortcut successfully updated.`)
    }
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const saveAction = isNewInstance ? createShortcut(shortcutState) : changeShortcut(shortcutState)
    dispatch(saveAction)
      .then(unwrapResult)
      .then(() => {
        handleSaveSuccess(isNewInstance)
      })
      .catch(error => {
        if (error) {
          setErrors({
            ...error
          })
        }
        SnackbarUtils.error(t`There was an error saving the shortcut.`)
      })
  }

  return (
    <SaveDialog
      open={open}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      loading={loading}
      title={t`Save shortcut`}
    >
      <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={loading}>
        <>
          <Box>
            <TextField
              required
              label={t`Title`}
              value={shortcutState.title_fi}
              onChange={e =>
                setShortcutState(prevState => ({
                  ...prevState,
                  title_fi: e.target.value
                }))
              }
              sx={{ width: '100%' }}
              error={errors.title_fi.length > 0}
              helperText={errors.title_fi.length > 0 ? errors.title_fi[0] : ''}
            />
          </Box>
          <Box>
            <TextField
              type="text"
              label={t`Description`}
              value={shortcutState.description_fi}
              multiline
              minRows={4}
              onChange={e =>
                setShortcutState(prevState => ({
                  ...prevState,
                  description_fi: e.target.value
                }))
              }
              sx={{ width: '100%' }}
              error={errors.description_fi.length > 0}
              helperText={errors.description_fi.length > 0 ? errors.description_fi[0] : ''}
            />
          </Box>
          <Box>
            <TextField
              required
              size="small"
              label={t`Link`}
              value={shortcutState.link}
              placeholder="https://waltti.fi"
              onChange={e =>
                setShortcutState(prevState => ({ ...prevState, link: e.target.value }))
              }
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <LinkIcon />
                    </InputAdornment>
                  )
                }
              }}
              sx={{ width: '100%' }}
              error={errors.link.length > 0}
              helperText={errors.link.length > 0 ? errors.link[0] : ''}
            />
          </Box>
          <Box>
            <TextField
              required
              size="small"
              type="number"
              label={t`Order on the page`}
              value={shortcutState.order}
              onChange={e =>
                setShortcutState(prevState => ({
                  ...prevState,
                  order: parseInt(e.target.value, 10)
                }))
              }
              sx={{ width: '100%' }}
              error={errors.order.length > 0}
              helperText={errors.order.length > 0 ? errors.order[0] : ''}
            />
          </Box>
        </>
      </StyledForm>
    </SaveDialog>
  )
}

export { SaveShortcutDialog }
